import { useCallback, useMemo, useRef, useEffect, memo } from 'react';
import { StickyZoneTextarea } from './StickyZoneTextarea';
import type { StickyZoneKey } from '@view-model/domain/key';
import { StickyZoneShape, type StickyZone, type StickyZoneText } from '../domain';
import { useStickyZoneContext } from '../adapter';
import type { RectShape } from '@model-framework/shape';
import { useD3DblClickCallback } from '@view-model/models/common/hooks';
import { D3Mouse } from '@view-model/models/common/d3/D3Mouse';
import { HandleD3Drag } from '@view-model/models/common/d3/HandleD3Drag';
import type { D3DragEvent } from 'd3-drag';
import type { Position, PositionPair } from '@view-model/models/common/types/ui';
import { LinkCreator } from '@view-model/models/common/components/LinkCreator';
import type { EditingUser } from '@model-framework/text/editing-user';
import { useOtherUserEditingToast } from '@model-framework/text/editing-user';
import type { Point } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic/Rect';
import type { StickyZoneId } from 'balus-common';
import { useHandler } from '@framework/hooks';

const MemoizedStickyZoneTextarea = memo(StickyZoneTextarea);

type Props = {
  readonly: boolean;
  stickyZone: StickyZone;
  position: Point;
  isHoverSelected: boolean;
  isSelected: boolean;
  isMultiSelected: boolean;
  showMenu: boolean;
  editing: boolean;
  editingUser: EditingUser | null;
  onEditStart(): void;
  onDblClickZone(position: Position, zoneKey: StickyZoneKey): void;
  onLinkerStart(startPosition: Position): void;
  onLinkerMove(currentPosition: Position): void;
  onLinkerEnd(currentPosition: Position): void;
  onRectSelection(rect: Rect): void;
  onRectSelectionEnd(rect: Rect, zoneId: StickyZoneId): void;
  shape: RectShape;
  checkDraggingMultiSelectionDisabled: (e: MouseEvent) => boolean;
};

export const StickyZoneContent: React.FC<Props> = ({
  readonly,
  stickyZone,
  position,
  isHoverSelected,
  isSelected,
  isMultiSelected,
  showMenu,
  editing,
  editingUser,
  onEditStart,
  onDblClickZone,
  onLinkerStart,
  onLinkerMove,
  onLinkerEnd,
  onRectSelection,
  onRectSelectionEnd,
  shape,
  checkDraggingMultiSelectionDisabled,
}: Props) => {
  const stickyZoneContext = useStickyZoneContext();

  const { width, height } = stickyZone.size.dump();
  const style = stickyZone.style;

  const nodeCreatableZoneRef = useRef<SVGGElement>(null);
  const pointsRef = useRef<PositionPair>({ start: null, end: null });
  const d3Mouse = useMemo(() => new D3Mouse(nodeCreatableZoneRef), []);
  const notifyOtherUserEditing = useOtherUserEditingToast();

  useEffect(() => {
    const onDragStart = function (event: D3DragEvent<Element, unknown, unknown>) {
      const [mouseX, mouseY] = d3Mouse.getPosition(event);
      pointsRef.current.start = position.addXY(mouseX, mouseY);
    };

    const onDrag = function (event: D3DragEvent<Element, unknown, unknown>) {
      const [mouseX, mouseY] = d3Mouse.getPosition(event);
      pointsRef.current.end = position.addXY(mouseX, mouseY);

      const { start, end } = pointsRef.current;
      if (start && end) {
        onRectSelection(Rect.fromPositions([start, end]));
      }
    };

    const onDragEnd = function () {
      const { start, end } = pointsRef.current;
      if (start && end) {
        onRectSelectionEnd(Rect.fromPositions([start, end]), stickyZone.id);
      }
      pointsRef.current = { start: null, end: null };
    };

    const handleD3Drag = new HandleD3Drag(nodeCreatableZoneRef, checkDraggingMultiSelectionDisabled);
    handleD3Drag.onMounted(onDrag, onDragStart, onDragEnd);
    return () => {
      handleD3Drag.onWillUnmounted();
    };
  }, [onRectSelectionEnd, d3Mouse, onRectSelection, position, stickyZone, checkDraggingMultiSelectionDisabled]);

  useD3DblClickCallback(
    nodeCreatableZoneRef,
    useCallback(
      (event) => {
        const [x, y] = d3Mouse.getPosition(event);
        const clickPosition = position.addXY(x, y).dump();
        onDblClickZone(clickPosition, stickyZone.key);
      },
      [d3Mouse, position, onDblClickZone, stickyZone]
    ),
    // 本来はハンドルする場合だけ伝搬を止めればいいが、現状ではズームが発動するのでstopPropagationで伝搬を止める
    true
  );

  const handleEditStart = useHandler(async function handleEditStart() {
    if (readonly) return;

    onEditStart();

    const started = await stickyZoneContext.handleEditingTextStart(stickyZone.text);

    if (!started) {
      notifyOtherUserEditing();
    }
  });

  const handleEditEnd = useHandler(async function handleEditEnd(text: StickyZoneText) {
    return stickyZoneContext.handleEditingTextConfirmed(text);
  });

  const handleChangeText = useHandler(async function handleChangeText(text: StickyZoneText) {
    return stickyZoneContext.handleEditingTextChanged(text);
  });

  const textareaSize = useMemo(() => ({ width, height }), [width, height]);
  const { fillColor, hoverFillColor } = style.getStyles();

  return (
    <>
      <g ref={nodeCreatableZoneRef}>
        {style.shape === StickyZoneShape.Rectangle
          ? shape.render({
              fillOpacity: 0.75,
              fill: isHoverSelected ? hoverFillColor : fillColor,
            })
          : shape.renderEllipse({
              fillOpacity: 0.75,
              fill: isHoverSelected ? hoverFillColor : fillColor,
              strokeWidth: 4,
              stroke: '#E0E0E0',
            })}
      </g>

      <MemoizedStickyZoneTextarea
        size={textareaSize}
        isMyEditing={editing}
        otherUserEditing={!!editingUser}
        textSelectable={isSelected && !isMultiSelected}
        text={stickyZone.text}
        fontSize={style.fontSize}
        textAlign={style.textAlign}
        shape={style.shape}
        titlePosition={style.titlePosition}
        onChange={handleChangeText}
        onEditStart={handleEditStart}
        onEditEnd={handleEditEnd}
        isHyperLink={!!stickyZone.url}
      />

      {showMenu && (
        <LinkCreator
          elementSize={{ width, height }}
          onLinkerStart={onLinkerStart}
          onLinkerMove={onLinkerMove}
          onLinkerEnd={onLinkerEnd}
        />
      )}
    </>
  );
};
